import React, { useState, useCallback } from 'react'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import styles from './accountToggle.module.scss'
import { AccountToggleDesktopProps, NavItem } from './types'
import { ACCOUNT_MENU_ID } from './constants'
import { getLocalizedPath } from './utils'
import { useClickOutside } from '../../hooks/use-click-outside'
import { ReactComponent as ArrowRightIcon } from '../../images/icons/icn-arrow-right.svg'
import Avatar from 'components/profile/Avatar'

const AccountToggleDesktop: React.FC<AccountToggleDesktopProps> = ({
  user,
  items,
  hasPendingMatches,
}) => {
  const { t } = useTranslation(['strCommonUI'])
  const [isOpen, setIsOpen] = useState(false)

  const handleToggle = useCallback(() => setIsOpen(prev => !prev), [])
  const handleClose = useCallback(() => setIsOpen(false), [])

  const handleClickOutside = useCallback(() => {
    if (isOpen) handleClose()
  }, [isOpen, handleClose])

  const popoverRef = useClickOutside<HTMLDivElement>(handleClickOutside)

  const handleItemClick = useCallback(
    (item: NavItem) => {
      handleClose()
      if (item.action) {
        item.action()
      }
    },
    [handleClose]
  )

  const renderFallbackIcon = () => (
    <span className={styles.avatarFallback}>
      {(user?.firstName?.[0] ?? '') + (user?.lastName?.[0] ?? '') || 'U'}
    </span>
  )

  return (
    <div
      className={styles.accountToggleDesktop}
      ref={popoverRef}
      data-testid='account-toggle-desktop'
    >
      <button
        className={classNames(styles.accountButton, {
          [styles.open]: isOpen,
          [styles.iconNotificationBadge]: hasPendingMatches,
        })}
        onClick={handleToggle}
        aria-haspopup='true'
        aria-expanded={isOpen}
        aria-controls={ACCOUNT_MENU_ID}
        aria-label={t('ariaLabel.accountMenu', 'Account Menu')}
      >
        <div className={styles.avatarContainer}>
          {user?.avatar ? <Avatar url={user.avatar} size='sm' /> : renderFallbackIcon()}
        </div>
      </button>

      {isOpen && (
        <div id={ACCOUNT_MENU_ID} className={styles.accountDropdown} role='menu'>
          <ul>
            {items.map(item => (
              <li
                key={item.id}
                role='none'
                className={classNames({
                  [styles.accountDropdownDivider]: item.isProfileItem,
                  [styles.withNotificationBadge]: hasPendingMatches && item.isProfileItem,
                })}
              >
                {item.isProfileItem ? (
                  <a
                    href={getLocalizedPath(item.path)}
                    className={styles.accountDropdownProfileLink}
                    onClick={handleClose}
                    role='menuitem'
                  >
                    <div className={styles.accountDropdownUserDetails}>
                      <span className={styles.accountDropdownUserName}>
                        {user?.fullName || t('userProfile', 'User Profile')}
                      </span>
                      {user?.wtnSingles && (
                        <span className={styles.accountDropdownUserWtn}>{user.wtnSingles}</span>
                      )}
                    </div>
                    <ArrowRightIcon className={styles.accountDropdownArrow} width='6' height='10' />
                  </a>
                ) : (
                  <a
                    href={getLocalizedPath(item.path)}
                    onClick={e => {
                      if (item.action) {
                        e.preventDefault()
                        handleItemClick(item)
                      } else {
                        handleClose()
                      }
                    }}
                    role='menuitem'
                  >
                    {item.name}
                    {hasPendingMatches && <span className={styles.notificationBadge}></span>}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export default React.memo(AccountToggleDesktop)
